import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogContent,
  Fade,
  IconButton,
  LinearProgress,
  Snackbar,
  Tab,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import useStyles from "./styles";
import * as StoreInventoryApi from "../../../api/storeInventoryApi";
import { isOk } from "../../../result";
import { emptyProduct, Product } from "../../../models/Product";
import {
  MinimalStoreInventoryItem,
  StoreInventoryItem,
  toMinimalStoreInventoryItem
} from "../../../models/StoreInventory";
import EditIcon from "@material-ui/icons/Edit";
import { Link, Sync } from "@material-ui/icons";
import DeleteIcon from "@material-ui/icons/Delete";
import {
  Alert,
  AlertTitle,
  TabContext,
  TabList,
  TabPanel
} from "@material-ui/lab";
import { ProductDeliveries } from "./ProductDeliveries";
import { ProductStocktaking } from "./ProductStocktaking";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { StoreInventoryItemEditor } from "../StoreList/components/StoreInventoryItemEditor";

import { useTheme } from "@material-ui/core/styles";

import * as InventoryApi from "../../../api/storeInventoryApi";
import * as ProductApi from "../../../api/productApi";

import { ConfirmDialog } from "../../../components/dialogs/ConfirmDialog";
import { ProductSpoilage } from "./ProductSpoilage";
import ErrorIcon from "@material-ui/icons/Error";
import { ReportSpoilage } from "./ReportSpoilage";
import { ProductOverview } from "./ProductOverview";
import { TenantProductEditor } from "../../tenants/Products/TenantProductEditor";
import { useDispatch, useSelector } from "react-redux";
import { thunkFetchProductsV2 } from "../../../store/products/action";
import { getSingleByGTINWithNoReports } from "../../../api/storeInventoryApi";
import { AppState } from "../../../store";
import { Store } from "../../../models/Store";
import { ConnectToPriceTag } from "../../tenants/Products/ConnectToPriceTag";
import { UserContext } from "../../../UserProvider";
import { Authenticated } from "../../../models/User";
import { ProductDetailSection } from "../../../components/product/ProductDetailSection";
import { ProductHeader } from "../../../components/product/ProductHeader";
import { useI18n } from "../../../hooks/useI18n";

interface ProductDetailsProps {
  storeId: string;
  storeName?: string;
  gtin: string;
  usesDigitalPriceTags?: boolean;
  onSave: (item: any, add: boolean) => void;
  onDelete: (productId: string) => void;
}

export const ProductDetails: FunctionComponent<ProductDetailsProps> = ({
  usesDigitalPriceTags,
  storeId,
  storeName,
  gtin,
  onSave,
  onDelete
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useStyles();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const currentUser = useContext(UserContext) as Authenticated;
  const { t } = useI18n();

  const allStores = useSelector<AppState, ReadonlyArray<Store>>(
    s => s.stores.stores
  );

  const [selectedTab, setSelectedTab] = useState("1");
  const [selectedStocktakingId, setSelectedStocktakingId] = useState("");
  const [reload, setReload] = useState<boolean>(false);

  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [showReportWaste, setShowReportWaste] = useState<boolean>(false);

  const [showAdd, setShowAdd] = useState({
    loading: false,
    display: false,
    success: false,
    showConnectToPriceTag: false
  });

  const [showRegister, setShowRegister] = useState({
    loading: false,
    display: false,
    success: false
  });

  const [showProductEditor, setShowProductEditor] = useState({
    loading: false,
    display: false,
    success: false,
    product: emptyProduct(),
    image: { url: "" }
  });

  const [loadingProduct, setLoadingProduct] = useState(false);

  const [showDelete, setShowDelete] = useState({
    loading: false,
    confirm: false
  });
  const [showSnackBar, setShowSnackbar] = useState({
    successBody: "",
    problem: null as any
  });
  const [inventoryItem, setInventoryItem] = useState<StoreInventoryItem>(
    {} as StoreInventoryItem
  );

  useEffect(() => {
    getProduct(false, false);
  }, []);

  useEffect(() => {
    if (showAdd.success || showProductEditor.success || reload) {
      getProduct(!reload, reload);
      setReload(false);
    }
  }, [showAdd.success, showProductEditor.success, reload]);

  const getProduct = (added: boolean, reload: boolean) => {
    StoreInventoryApi.getSingleByGTINWithNoReports(storeId, gtin).then(
      result => {
        if (isOk(result)) {
          setInventoryItem(result.value);

          populateDeliveryStocktakingAndSpoilage(result.value).then(result => {
            if (added || reload)
              onSave({ ...result, productId: result.product.id }, added);
          });
        } else {
          if (result.details.status === 404) {
            // Let's show add if product not found
            if (result.details.detail === "ProductNotRegistered") {
              setShowRegister({ ...showRegister, display: true });
            } else {
              setShowAdd({ ...showAdd, display: true });
            }
          } else {
            setShowSnackbar({ successBody: "", problem: result.details });
          }
        }
      }
    );
  };

  const populateDeliveryStocktakingAndSpoilage = async (
    item: StoreInventoryItem
  ) => {
    setLoadingProduct(true);
    const result = await StoreInventoryApi.getDeliveryStockTakingAndSpoilage(
      storeId,
      item.product.id
    );
    if (isOk(result)) {
      item = {
        ...item,
        onShelf: result.value.onShelf,
        reportedProductDelivery: result.value.reportedProductDelivery,
        reportedProductSpoilage: result.value.reportedProductSpoilage,
        reportedStocktaking: result.value.reportedStocktaking
      };
      setInventoryItem(item);
    } else {
      setShowSnackbar({ successBody: "", problem: result.details });
    }
    setLoadingProduct(false);

    return item;
  };

  const syncPriceTag = async () => {
    const result = await InventoryApi.syncPriceTag(
      storeId,
      inventoryItem.product.id
    );

    if (isOk(result)) {
      return setShowSnackbar({
        problem: null,
        successBody: t("common.messages.digitalPriceTagSynced")
      });
    }

    return setShowSnackbar({
      successBody: "",
      problem: result.details
    });
  };

  const deleteProductFromStore = async () => {
    setShowDelete({ ...showDelete, loading: true });

    const result = await InventoryApi.remove(storeId, inventoryItem.product.id);

    setShowDelete({ ...showDelete, loading: false, confirm: false });

    if (isOk(result)) {
      return onDelete(inventoryItem.product.id);
    }

    return setShowSnackbar({
      successBody: "",
      problem: result.details
    });
  };

  const formatItemToUpdate = (): MinimalStoreInventoryItem => {
    let item = toMinimalStoreInventoryItem(inventoryItem);
    item.productId = inventoryItem.product.id;
    item.articleNumber =
      inventoryItem.articleNumber ?? inventoryItem.product.articleNumber;
    item.orderUnit =
      inventoryItem.orderUnit ?? inventoryItem.product.orderInfo?.orderUnit;
    item.price = inventoryItem.price ?? inventoryItem.product.pricing.price;
    item.maxOnShelf =
      inventoryItem.maxOnShelf ?? inventoryItem.product.orderInfo?.maxOnShelf;
    item.orderThreshold =
      inventoryItem.orderThreshold ??
      inventoryItem.product.orderInfo?.orderThreshold;
    item.priority = inventoryItem.priority ?? inventoryItem.product.priority;
    item.exists = true;

    return item;
  };

  const createStoreProduct = () => {
    let product = { ...inventoryItem.product } as any;
    product.articleNumber =
      inventoryItem.articleNumber ?? inventoryItem.product.articleNumber;
    product.pricing.price =
      inventoryItem.price ?? inventoryItem.product.pricing.price;
    if (product.orderInfo) {
      product.orderInfo.orderUnit =
        inventoryItem.orderUnit ?? inventoryItem.product.orderInfo?.orderUnit;
      product.orderInfo.maxOnShelf =
        inventoryItem.maxOnShelf ?? inventoryItem.product.orderInfo?.maxOnShelf;
      product.orderInfo.orderThreshold =
        inventoryItem.orderThreshold ??
        inventoryItem.product.orderInfo?.orderThreshold;
    }
    product.priority = inventoryItem.priority ?? inventoryItem.product.priority;
    product.onShelf = inventoryItem.onShelf;

    return product;
  };

  return inventoryItem &&
    inventoryItem.product &&
    inventoryItem.product.name ? (
    <>
      <TabContext value={selectedTab}>
        <ProductHeader
          storeProduct
          product={inventoryItem.product}
          setShowEdit={show => setShowEdit(show)}
          setShowDelete={show =>
            setShowDelete({ ...showDelete, confirm: show })
          }
          setShowSyncPriceTag={
            usesDigitalPriceTags ? () => syncPriceTag() : undefined
          }
          setShowReportWaste={show => setShowReportWaste(show)}
          setSelectedTab={(event: React.ChangeEvent<{}>, newValue: string) => {
            setSelectedTab(newValue);
            setSelectedStocktakingId("");
          }}
        />
        <div style={{ marginTop: 15, marginBottom: 20 }}>
          <TabPanel value="4" className={classes.productTab}>
            <ProductSpoilage
              inventoryItem={inventoryItem}
              storeId={storeId}
              onSpoilageClick={storeStocktakingId => {
                setSelectedTab("3");
                setSelectedStocktakingId(storeStocktakingId);
              }}
              onSave={(reports: any, removedQuantity: number) => {
                const item = {
                  ...inventoryItem,
                  productId: inventoryItem.product.id,
                  onShelf: inventoryItem.onShelf + removedQuantity,
                  reportedProductSpoilage: reports
                };
                setInventoryItem(item);
                onSave(item, false);
              }}
            />
          </TabPanel>
          <TabPanel value="3" className={classes.productTab}>
            <ProductStocktaking
              inventoryItem={inventoryItem}
              selectedStocktakingId={selectedStocktakingId}
              storeId={storeId}
              onSave={reloadProduct => setReload(reloadProduct)}
            />
          </TabPanel>
          <TabPanel value="2" className={classes.productTab}>
            <ProductDeliveries
              inventoryItem={inventoryItem}
              storeId={storeId}
              onSave={(deliveries: any, quantity) => {
                const item = {
                  ...inventoryItem,
                  productId: inventoryItem.product.id,
                  onShelf: quantity,
                  reportedProductDelivery: deliveries
                };
                setInventoryItem(item);
                onSave(item, false);
              }}
            />
          </TabPanel>
          <TabPanel value="1" className={classes.productTab}>
            <ProductDetailSection
              product={createStoreProduct()}
              mobile={mobile}
            />
          </TabPanel>
        </div>
      </TabContext>

      <ConfirmDialog
        visible={showDelete.confirm}
        titleText={t("common.messages.removeProductConfirmTitle")}
        bodyText={t("common.messages.removeProductConfirmBody")}
        confirmText={t("common.messages.removeProductConfirmButton")}
        onConfirm={() => {
          deleteProductFromStore();
          return true;
        }}
        onCancel={() => {
          setShowDelete({ ...showDelete, confirm: false });
        }}
      />

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"md"}
        open={showEdit}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setShowEdit(false);
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          {allStores && allStores.length > 1 ? (
            <StoreInventoryItemEditor
              storeId={storeId}
              inventoryItem={formatItemToUpdate()}
              onSave={(updatedItem: any) => {
                setInventoryItem(updatedItem);
                getProduct(true, true); // some browsers have issues with updating the state.
                onSave(
                  {
                    ...updatedItem,
                    price:
                      updatedItem.price ?? inventoryItem.product.pricing.price
                  },
                  false
                );

                setShowEdit(false);
                setShowSnackbar({
                  ...showSnackBar,
                  successBody: t("common.messages.productSuccessfullyUpdated")
                });
              }}
            />
          ) : (
            <TenantProductEditor
              editingProduct={inventoryItem.product}
              onSave={product => {
                setInventoryItem({ ...inventoryItem, product: product });
                setShowEdit(false);
                setShowSnackbar({
                  ...showSnackBar,
                  successBody: t("common.messages.productSuccessfullyUpdated")
                });
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"sm"}
        open={showReportWaste}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <Typography variant="h5">
            {t("common.messages.reportSpoilage")}
          </Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setShowReportWaste(false);
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <ReportSpoilage
            storeId={storeId}
            inventoryItem={formatItemToUpdate()}
            onSave={report => {
              setShowReportWaste(false);
              const newReportedSpoilage = [report];
              const currentReportedSpoilage =
                inventoryItem.reportedProductSpoilage
                  ? inventoryItem.reportedProductSpoilage
                  : [];
              const item = {
                ...inventoryItem,
                onShelf: inventoryItem.onShelf - report.quantity,
                productId: inventoryItem.product.id,
                reportedProductSpoilage: [
                  ...newReportedSpoilage,
                  ...currentReportedSpoilage
                ]
              };
              setInventoryItem(item);
              setShowSnackbar({
                ...showSnackBar,
                successBody: t("common.messages.wasteReportSaved")
              });
              onSave(item, false);
            }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={showSnackBar.successBody != ""}
        autoHideDuration={2000}
        onClose={() => {
          setShowSnackbar({ ...showSnackBar, successBody: "" });
        }}
      >
        <Alert
          onClose={() => setShowSnackbar({ ...showSnackBar, successBody: "" })}
          severity="success"
        >
          {showSnackBar.successBody ?? ""}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showSnackBar.problem != null}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar({ ...showSnackBar, problem: null })}
      >
        <Alert severity="error">
          <AlertTitle>
            {showSnackBar.problem?.status}, {showSnackBar.problem?.title}
          </AlertTitle>
          {showSnackBar.problem?.detail}{" "}
          <strong>{showSnackBar.problem?.traceId}</strong>
        </Alert>
      </Snackbar>
    </>
  ) : (
    <div>
      {showAdd.display ? (
        <div style={{ margin: 10 }}>
          <Alert
            severity="warning"
            style={{ display: "flex", justifyContent: "center" }}
          >
            {t("common.messages.productNotRegisteredInStore")}{" "}
            <b>{storeName}</b>.
            {currentUser.tenant?.configuration?.features?.digitalPriceTags
              ? ` ${t("common.messages.clickConnectToPriceTagOrActivate")}`
              : ` ${t("common.messages.clickActivateToRegister")}`}
          </Alert>
          <div
            style={{
              display: "flex",
              marginTop: 20,
              justifyContent: "center"
            }}
          >
            {currentUser.tenant?.configuration?.features?.digitalPriceTags && (
              <>
                <Button
                  color="primary"
                  startIcon={<Link />}
                  variant="contained"
                  onClick={() =>
                    setShowAdd({ ...showAdd, showConnectToPriceTag: true })
                  }
                >
                  {t("common.messages.connectToPriceTag")}
                </Button>
                <Typography
                  variant={"body1"}
                  style={{
                    marginLeft: 15,
                    marginRight: 15,
                    alignSelf: "center"
                  }}
                >
                  {t("common.messages.or")}
                </Typography>
              </>
            )}
            <Button
              style={{ width: 200 }}
              disabled={showAdd.loading}
              variant="contained"
              color="primary"
              onClick={async () => {
                setShowAdd({ ...showAdd, loading: true });
                const result = await StoreInventoryApi.add(storeId, {
                  gtin: gtin
                });
                if (result.type === "OK") {
                  setShowSnackbar({
                    ...showSnackBar,
                    successBody: `${t(
                      "common.messages.productRegisteredInStore"
                    )} ${storeName}!`
                  });
                  setShowAdd({
                    ...showAdd,
                    loading: false,
                    display: false,
                    success: true
                  });
                } else {
                  setShowAdd({ ...showAdd, display: false });
                  setShowSnackbar({ successBody: "", problem: result.details });
                }
              }}
            >
              {t("common.messages.activate")}{" "}
              {showAdd.loading && (
                <CircularProgress size={20} style={{ marginLeft: 20 }} />
              )}
            </Button>
          </div>

          <Dialog
            fullWidth={true}
            fullScreen={mobile}
            maxWidth={"md"}
            open={showAdd.showConnectToPriceTag}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <MuiDialogTitle disableTypography>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={() => {
                  setShowAdd({ ...showAdd, showConnectToPriceTag: false });
                }}
              >
                <CloseIcon />
              </IconButton>
            </MuiDialogTitle>
            <DialogContent>
              <ConnectToPriceTag
                addToStore={true}
                storeId={storeId}
                gtin={gtin}
                onSuccess={() =>
                  setShowAdd({
                    ...showAdd,
                    showConnectToPriceTag: false,
                    display: false,
                    success: true
                  })
                }
                onCancel={() =>
                  setShowAdd({
                    ...showAdd,
                    showConnectToPriceTag: false
                  })
                }
              />
            </DialogContent>
          </Dialog>
        </div>
      ) : showRegister.display ? (
        <div style={{ margin: 10 }}>
          <Alert
            severity="warning"
            style={{ display: "flex", justifyContent: "center" }}
          >
            {t("common.messages.productNotRegisteredInSystem")}
          </Alert>
          <div
            style={{
              display: "flex",
              marginTop: 20,
              justifyContent: "center"
            }}
          >
            <Button
              style={{ width: 200 }}
              disabled={loadingProduct === true}
              variant="contained"
              color="primary"
              onClick={async () => {
                setLoadingProduct(true);
                const result = await ProductApi.lookup(gtin);
                setLoadingProduct(false);
                if (result.type === "OK") {
                  setShowRegister({ ...showRegister, display: false });
                  setShowProductEditor({
                    ...showProductEditor,
                    display: true,
                    product: result.value.product ?? {
                      ...emptyProduct(),
                      gtin: gtin
                    },
                    image: result.value.imageData
                  });
                } else {
                  setShowRegister({ ...showRegister, display: false });
                  setShowProductEditor({
                    ...showProductEditor,
                    display: true,
                    product: { ...emptyProduct(), gtin: gtin }
                  });
                }
              }}
            >
              {t("common.messages.register")}{" "}
              {loadingProduct && (
                <CircularProgress size={20} style={{ marginLeft: 20 }} />
              )}
            </Button>
          </div>
        </div>
      ) : showProductEditor.display ? (
        <div style={{ margin: mobile ? 0 : 20 }}>
          <TenantProductEditor
            adding={false}
            image={showProductEditor.image}
            editingProduct={showProductEditor.product}
            onSave={async () => {
              dispatch(thunkFetchProductsV2());
              setShowProductEditor({
                ...showProductEditor,
                display: false,
                success: true
              });

              setShowAdd({
                ...showAdd,
                loading: false,
                display: true
              });
            }}
          />
        </div>
      ) : showSnackBar.problem != null ? (
        <Alert
          severity="error"
          style={{ display: "flex", justifyContent: "center", marginTop: 10 }}
        >
          <AlertTitle>
            {showSnackBar.problem?.status}, {showSnackBar.problem?.title}
          </AlertTitle>
          {showSnackBar.problem?.detail}{" "}
        </Alert>
      ) : (
        <Fade
          in={true}
          style={{
            minHeight: 100,
            transitionDelay: "800ms"
          }}
          unmountOnExit
        >
          <div style={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress size={80} />
          </div>
        </Fade>
      )}
    </div>
  );
};
